import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useAtom } from 'jotai';
import { documentFilterState } from '@/recoil/atom';
import { getPeriodList } from '@/utils/date';
import { getFormattedToday } from '@/utils/format';
import DateRangePicker from '@/components/Approval/@components/DateRangePicker';
import { RadioV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';

type Props = {
  filterType: 'created' | 'meeting';
};

const DIRECT_TYPE = 3;

/**
 * 회의록 기간(회의 일시 / 등록기간) 편집 패널 — 전자결재 PeriodFilterPanel 과 동일 디자인.
 * 세로 라디오 프리셋 + '직접 선택' 시 날짜 범위 입력(달력/지우기).
 * 값은 documentFilterState 의 created/meeting Started·Ended 를 직접 갱신.
 */
const DocumentPeriodPanel = ({ filterType }: Props) => {
  const [filters, setFilters] = useAtom(documentFilterState);

  const startField =
    filterType === 'created' ? 'createdStartedAt' : 'meetingStartedAt';
  const endField =
    filterType === 'created' ? 'createdEndedAt' : 'meetingEndedAt';

  const start = filters[startField];
  const end = filters[endField];

  const periodList = getPeriodList();
  const presets = periodList.filter((p) => p.type !== DIRECT_TYPE);
  const directPreset = periodList.find((p) => p.type === DIRECT_TYPE);

  const matched = presets.find((p) => p.value === start && p.value2 === end);
  const hasDirectValue = (!!start || !!end) && !matched;
  // 날짜를 모두 지워도 패널이 열려 있는 동안은 다시 입력할 수 있게 '직접 선택'을 기억한다
  const [directMode, setDirectMode] = useState(hasDirectValue);
  const isDirect = directMode || hasDirectValue;
  // 직접 입력 중에는 값이 프리셋과 우연히 같아져도 '직접 선택' 하나만 체크한다(라디오 중복 방지)
  const selectedType = isDirect ? DIRECT_TYPE : matched?.type ?? null;

  const setRange = (nextStart?: string, nextEnd?: string) => {
    setFilters({
      ...filters,
      [startField]: nextStart,
      [endField]: nextEnd,
    });
  };

  // 라디오는 단일 선택이라 이미 선택된 항목을 다시 눌러도 해제하지 않는다
  // (직접 선택해 둔 날짜가 프리셋 기본값으로 되돌아가지 않도록 아무 동작도 하지 않는다)
  const selectPreset = (type: number, value?: string, value2?: string) => {
    if (selectedType === type) return;
    setDirectMode(type === DIRECT_TYPE);
    setRange(value, value2);
  };

  // DateRangePicker 가 보류해 둔 반대쪽 값까지 잇달아 올릴 수 있어 이전 값 기준으로 누적한다
  const setOne = (which: 'start' | 'end', value: string) => {
    setFilters((prev) => ({
      ...prev,
      [which === 'start' ? startField : endField]: value || undefined,
    }));
  };

  // 달력도 키보드 입력과 같은 규칙(과거 제한 없음, 시작~종료 1년 이내)을 따르도록 min 은 두지 않는다
  const max = getFormattedToday();

  return (
    <div>
      <List>
        {presets.map((p) => (
          <Row
            key={`period-${p.type}`}
            className={'v2rd-hover-target'}
            onClick={() => selectPreset(p.type, p.value, p.value2)}
          >
            <RadioV2 checked={selectedType === p.type} sx={{ padding: 0 }} />
            <Label>{p.name}</Label>
          </Row>
        ))}
        <Row
          className={'v2rd-hover-target'}
          onClick={() =>
            selectPreset(DIRECT_TYPE, directPreset?.value, directPreset?.value2)
          }
        >
          <RadioV2
            checked={selectedType === DIRECT_TYPE}
            sx={{ padding: 0 }}
          />
          <Label>{directPreset?.name}</Label>
        </Row>
      </List>
      {isDirect && (
        <>
          <Line />
          <DateWrap>
            <DateRangePicker
              start={start}
              end={end}
              max={max}
              onChange={setOne}
            />
          </DateWrap>
        </>
      )}
    </div>
  );
};

export default DocumentPeriodPanel;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  * {
    cursor: pointer;
  }
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text, black);
`;

const Line = styled.div`
  height: 1px;
  background: var(--dk-bg-strong, #e7e9ec);
  margin: 8px 0;
`;

const DateWrap = styled.div`
  padding: 8px;
`;
